/**
 * What a seller check says, and what it does not.
 *
 * ONE COPY, FOR EVERY CLIENT. The specification forbids representing a seller
 * check as a guarantee of title — and the way a product breaks that rule is not
 * by writing "guaranteed". It is by writing "Verified" alone and letting the
 * reader supply the meaning. So the word never appears without what was
 * checked, and never without VERIFIED_LIMIT.
 *
 * A sentence with legal weight that is typed out twice is a sentence that will
 * eventually say two different things, and the one that drifts will be the one
 * somebody reads before the largest payment of their life. Take both or take
 * neither.
 *
 * WHY IT IS NOT IN schemas/sales.ts, where it used to live. That file imports
 * zod, and the web client has no zod dependency at all — importing it there to
 * reach three strings would have put a schema validator in the bundle of a
 * marketing page this codebase has spent real effort keeping small for a Kenyan
 * mobile connection. These are copy, not shapes, so they belong in a file that
 * needs nothing. schemas/sales.ts re-exports them, so every existing import
 * keeps working.
 *
 * TRANSLATION: deliberately left in English by the Swahili pass (locales/sw.ts
 * says so at length). A rough rendering of a limitation is worse than a
 * limitation in a second language — a buyer who half-understands "this is not a
 * guarantee" may read it as a guarantee. They need a qualified translator with
 * a Kenyan advocate's eye before they are offered in Swahili.
 */

/**
 * What was actually checked, phrased for the capacity the seller claimed.
 *
 * Never "Verified" on its own, and never without a date beside it at the call
 * site: a badge with no content is exactly the thing the rule above forbids.
 */
export function verifiedLine(capacity: string | null | undefined): string {
  switch (capacity) {
    case 'owner': return 'We checked that the seller is the owner';
    case 'agent': return 'We checked the agent’s authority to sell';
    case 'developer': return 'We checked the developer’s approvals for this development';
    case 'administrator': return 'We checked the appointment to administer this estate';
    default: return 'We checked who is selling this property';
  }
}

export const VERIFIED_LIMIT =
  'That is a check of who is selling, not of the title itself. It is not a guarantee, '
  + 'and it does not replace your own advocate’s search before you pay anything.';

/**
 * And what an UNCHECKED advert means, which is not "suspect".
 *
 * Nobody has asked us to check it yet. Saying so plainly is what keeps the
 * filter from turning into an accusation against every seller who has not paid
 * for, or not yet been offered, a check.
 */
export const UNCHECKED_MEANING =
  'Nobody has checked who is selling this one yet. That does not make it suspect — '
  + 'ask the seller for their title and speak to your own advocate.';
